/**
 * CompanyGrid - Mobile card-based grid view for company list
 * Mirrors Angular company-list grid view
 * Styled to match InviteeGrid/ApplicationGrid design
 *
 * Company name opens **React** company edit (same as `CompanyTable`). Angular’s read-only
 * dashboard is `/company/:id` — there is no React equivalent; see `COMPANY_MODULE_MIGRATION_README.md`.
 */
import { Eye } from 'lucide-react'
import { Link } from 'react-router-dom'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { cn } from '@/lib/utils/helpers'
import type { CompanyGridProps } from '../types'
import { useUserRole } from '@/hooks/useUserRole'
import { formatPafSummaryDate } from '../utils/pafDateFormat'

export function CompanyGrid({ companies, onToggleListed, isCompanyActive, disabled = false, loading = false }: CompanyGridProps) {
    const { isLibertyRole, hasPolicy } = useUserRole()
    const canEditListed = hasPolicy(['company_listed_edit'])
    const listedDisabled = disabled || !canEditListed

    if (loading) {
        return (
            <ModuleScreenLoading
                variant="inline"
                className="py-16"
                data-testid="company-list-loading"
            />
        )
    }

    if (companies.length === 0) {
        return (
            <div
                className="bg-white p-12 text-center"
                data-testid="company-list-empty-state"
            >
                <p className="text-slate-500">No companies found</p>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4" data-testid="company-grid">
            {companies.map((company) => {
                const active = isCompanyActive ? isCompanyActive(company) : company.org_listed_ids?.['1'] === 1
                return (
                <div
                    key={company.company_id}
                    data-testid={`company-grid-card-${company.company_id}`}
                    className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                    <div className="p-4 space-y-3">
                        {/* Company Name */}
                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Company Name</p>
                            <Link
                                data-testid={`company-grid-card-${company.company_id}-name-link`}
                                to={`/company/edit/${company.company_id}`}
                                className="text-[rgb(21_151_255)] hover:text-[#0d7acc] font-semibold text-sm hover:underline transition-colors text-left block"
                            >
                                {company.company_name}
                            </Link>

                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">ID</p>
                                <p className="text-sm text-slate-700">{company.company_id}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">State</p>
                                <p className="text-sm text-slate-700">{company.company_state || '—'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">City</p>
                                <p className="text-sm text-slate-700">{company.company_city || '—'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact Name</p>
                                <p className="text-sm text-slate-700">{company.contact_name || '—'}</p>
                            </div>
                        </div>

                        <div>
                            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Contact Email</p>
                            <p className="text-sm text-slate-700 truncate">{company.contact_email || '—'}</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">LR Relationship</p>
                                <p className="text-sm text-slate-700">{company.salesperson || '—'}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Unit Count</p>
                                <p className="text-sm text-slate-700">{company.units_number ?? '—'}</p>
                            </div>
                        </div>

                        {isLibertyRole && (
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Alias Name</p>
                                    <p className="text-sm text-slate-700">{company.company_alias_name || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">ITR Value</p>
                                    <p className="text-sm text-slate-700">{company.itrratio ?? '—'}</p>
                                </div>
                                {company.deleted_at && (
                                    <div>
                                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Deleted</p>
                                        <p className="text-sm text-slate-700 whitespace-nowrap">
                                            {formatPafSummaryDate(company.deleted_at) || '—'}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between bg-slate-50/50">
                        <button
                            data-testid={`company-grid-card-${company.company_id}-liberty-listed-toggle`}
                            onClick={() => onToggleListed?.(company)}
                            disabled={listedDisabled}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold transition-all ${active
                                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                                : 'bg-red-100 text-red-700 hover:bg-red-200'
                                } ${listedDisabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                            type="button"
                        >
                            {active ? 'Active' : 'Not Active'}
                        </button>
                        <Link
                            data-testid={`company-grid-card-${company.company_id}-edit-link`}
                            to={`/company/edit/${company.company_id}`}
                            className={cn(primaryEntityActionButtonClass, 'h-8 min-w-0 gap-1.5 px-3 text-xs')}
                            title="Open company edit (React)"
                        >
                            <Eye className="h-3.5 w-3.5" />
                            Edit
                        </Link>
                    </div>
                </div>
                )
            })}
        </div>
    )
}
